import { useEffect, useMemo, useState } from 'react';
import { PlusCircle, Pencil, Trash2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader } from '@/components/loader';
import { useToast } from '@/components/ui/use-toast';
import { formatNumber } from 'shared/utils';
import { useSynonyms } from 'shared/hooks/search-synonyms.hooks';
import { SynonymFormDialog } from './components/synonym-form-dialog';
import { DeleteSynonymDialog } from './components/delete-synonym-dialog';
import { ImportSynonymsDialog } from './components/import-synonyms-dialog';

const ALL = 'all';

const TermChips = ({ terms }) => {
  if (!terms?.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {terms.map(term => (
        <Badge key={term} variant="secondary" className="font-normal">
          {term}
        </Badge>
      ))}
    </div>
  );
};

const formatUpdatedAt = iso => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

export default function SearchSynonyms() {
  const { toast } = useToast();
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const mappingType = typeFilter === ALL ? undefined : typeFilter;
  const { data: synonyms = [], isLoading, error } = useSynonyms(mappingType);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Failed to load synonyms',
        description: error?.response?.data?.message || error?.message || 'Unexpected error',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return synonyms;
    const matches = term => term.toLowerCase().includes(q);
    return synonyms.filter(
      s =>
        s.synonyms?.some(matches) ||
        s.input?.some(matches) ||
        (s.note || '').toLowerCase().includes(q),
    );
  }, [synonyms, search]);

  return (
    <Card>
      <div className="flex items-start justify-between">
        <CardHeader>
          <CardTitle>Search Synonyms</CardTitle>
          <CardDescription>
            Global synonym mappings for search. Applied across all organizations.
          </CardDescription>
        </CardHeader>
        <div className="flex gap-2 p-6">
          <ImportSynonymsDialog />
          <Button onClick={() => setAddOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add mapping
          </Button>
        </div>
      </div>

      <CardContent>
        <div className="mb-4 flex gap-2">
          <Input
            placeholder="Filter by synonym, input or note"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              <SelectItem value="equivalent">Equivalent</SelectItem>
              <SelectItem value="explicit">Explicit</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="mb-2 text-sm text-muted-foreground">
              {formatNumber(filtered.length)} mapping(s)
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Synonyms</TableHead>
                  <TableHead>Input</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Last updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No synonym mappings found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map(record => (
                    <TableRow key={record.id}>
                      <TableCell>
                        <Badge variant={record.mappingType === 'explicit' ? 'outline' : 'default'}>
                          {record.mappingType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TermChips terms={record.synonyms} />
                      </TableCell>
                      <TableCell>
                        <TermChips
                          terms={record.mappingType === 'explicit' ? record.input : null}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{record.note || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <div>{formatUpdatedAt(record.updatedAt)}</div>
                        {record.updatedBy?.email && <div>{record.updatedBy.email}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Edit mapping"
                            onClick={() => setEditing(record)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Delete mapping"
                            onClick={() => setDeleting(record)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>

      <SynonymFormDialog open={addOpen} onOpenChange={setAddOpen} />
      <SynonymFormDialog
        open={!!editing}
        record={editing}
        onOpenChange={open => !open && setEditing(null)}
      />
      <DeleteSynonymDialog record={deleting} onOpenChange={open => !open && setDeleting(null)} />
    </Card>
  );
}
